/**
 * ══════════════════════════════════════════════════════════════════════════════
 * PTEL DESIGN SYSTEM v2.0 - TypeBadge Component
 * ══════════════════════════════════════════════════════════════════════════════
 *
 * Badge para identificar tipologías de infraestructuras críticas PTEL.
 * 9 variantes con colores Material Design.
 *
 * Uso:
 *   <TypeBadge code="san" />
 *   <TypeBadge code="edu" showLabel={false} />
 *
 * ══════════════════════════════════════════════════════════════════════════════
 */

import { colors, typography, borderRadius, components } from '../design-tokens';
import type { TypologyCode } from '../design-tokens';

// ─────────────────────────────────────────────────────────────────────────────
// TIPOS
// ─────────────────────────────────────────────────────────────────────────────

export interface TypeBadgeProps {
  /** Código de tipología (san/edu/pol/cul/rel/eme/dep/adm/inf) */
  code: TypologyCode;
  /** Mostrar etiqueta de texto (por defecto: true) */
  showLabel?: boolean;
  /** Tamaño del badge */
  size?: 'sm' | 'md';
  /** Clases CSS adicionales */
  className?: string;
  /** ID para testing */
  testId?: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// CONFIGURACIÓN DE TAMAÑOS
// ─────────────────────────────────────────────────────────────────────────────

const sizeConfig = {
  sm: {
    width: '72px',
    padding: '3px 0',
    fontSize: typography.fontSize['2xs'], // 10px
  },
  md: {
    width: components.typeBadge.width, // 84px
    padding: '4px 0',
    fontSize: typography.fontSize.xs, // 11px
  },
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// COMPONENTE
// ─────────────────────────────────────────────────────────────────────────────

export function TypeBadge({
  code,
  showLabel = true,
  size = 'md',
  className = '',
  testId,
}: TypeBadgeProps) {
  const typology = colors.typologies[code];
  const sizeStyles = sizeConfig[size];

  // Fallback para códigos desconocidos
  if (!typology) {
    return (
      <span
        data-testid={testId ?? `type-badge-unknown`}
        className={className}
        style={{
          display: 'inline-block',
          width: sizeStyles.width,
          textAlign: 'center',
          fontSize: sizeStyles.fontSize,
          fontFamily: typography.fontFamily.sans,
          fontWeight: typography.fontWeight.medium,
          padding: sizeStyles.padding,
          borderRadius: borderRadius.sm,
          backgroundColor: 'rgba(113, 113, 122, 0.15)',
          color: colors.fg.muted,
        }}
      >
        {showLabel ? code.toUpperCase() : ''}
      </span>
    );
  }

  return (
    <span
      data-testid={testId ?? `type-badge-${code}`}
      data-typology={code}
      className={className}
      style={{
        display: 'inline-block',
        width: sizeStyles.width,
        textAlign: 'center',
        fontSize: sizeStyles.fontSize,
        fontFamily: typography.fontFamily.sans,
        fontWeight: typography.fontWeight.medium,
        padding: sizeStyles.padding,
        borderRadius: borderRadius.sm,
        backgroundColor: typology.bgAlpha,
        color: typology.color,
      }}
    >
      {showLabel ? typology.name : ''}
    </span>
  );
}

export default TypeBadge;
